import { useMemo, useState } from "react";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import CaseCard from "@/components/feature/CaseCard";
import { caseCategories, caseStatuses, cases } from "@/mocks/cases";
import { siteInfo } from "@/mocks/site";

export default function CasesPage() {
  const [category, setCategory] = useState("全部");
  const [status, setStatus] = useState("全部");
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return cases.filter((item) => {
      const matchCategory = category === "全部" || item.category === category;
      const matchStatus = status === "全部" || item.status === status;
      const matchKeyword =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw) ||
        item.region.toLowerCase().includes(kw);
      return matchCategory && matchStatus && matchKeyword;
    });
  }, [category, status, keyword]);

  const reset = () => {
    setCategory("全部");
    setStatus("全部");
    setKeyword("");
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "事件案例库",
    description:
      "按分类与核验状态整理的社会爆料事件案例，包含反转进展、官方回应与核验结论。",
    url: `${siteInfo.url}/cases`,
    inLanguage: "zh-CN",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: cases.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.title,
        url: `${siteInfo.url}/cases/${item.slug}`,
      })),
    },
  };

  return (
    <>
      <Seo
        title="事件案例库｜社会爆料反转与官方回应梳理 - 棱镜求证"
        description="按分类与核验状态检索社会爆料事件案例，查看每起事件的反转进展、官方回应、核验结论与参考来源，支持关键词搜索与结论筛选。"
        keywords="社会爆料案例,社会爆料反转,官方回应,辟谣案例,真相核验"
        path="/cases"
        jsonLd={jsonLd}
      />

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "事件案例库", path: "/cases" }]} />
          <h1 className="mt-5 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            事件案例库
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            每一起事件都按“爆料—发酵—回应—核验”的顺序整理。你可以按分类、核验状态或关键词筛选，
            快速找到正在追踪或已经形成结论的案例。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6">
          <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-1.5">
                {caseCategories.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setCategory(item)}
                    className={`cursor-pointer rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                      category === item
                        ? "bg-primary-500 text-white"
                        : "bg-background-50 text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>

              <div className="flex flex-col gap-3 border-t border-background-200 pt-4 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400" aria-hidden="true">
                    <i className="ri-search-line text-base" />
                  </span>
                  <input
                    type="search"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder="搜索事件标题、地区或关键词"
                    aria-label="搜索事件案例"
                    className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>

                <div className="relative">
                  <select
                    value={status}
                    onChange={(event) => setStatus(event.target.value)}
                    aria-label="按核验状态筛选"
                    className="w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-2.5 pl-3 pr-9 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 sm:w-44"
                  >
                    {caseStatuses.map((item) => (
                      <option key={item} value={item}>
                        {item === "全部" ? "全部核验状态" : item}
                      </option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400" aria-hidden="true">
                    <i className="ri-arrow-down-s-line text-base" />
                  </span>
                </div>

                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-refresh-line text-base" aria-hidden="true" />
                  <span className="whitespace-nowrap">重置</span>
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 起案例
            </p>
            <p className="hidden text-xs text-foreground-500 sm:block">
              数据更新至 2026-09-10
            </p>
          </div>

          {filtered.length === 0 ? (
            <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500" aria-hidden="true">
                <i className="ri-search-eye-line text-2xl" />
              </span>
              <p className="mt-4 font-heading text-base font-bold text-foreground-950">没有匹配的案例</p>
              <p className="mt-2 text-sm text-foreground-600">试试更换关键词或重置筛选条件。</p>
              <button
                type="button"
                onClick={reset}
                className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line text-base" aria-hidden="true" />
                重置筛选
              </button>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((item) => (
                <CaseCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}